import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@src/store/rootState";
import type {
  BuildStep,
  BuildStepStatus,
  TestBuild,
  TestBuildsState,
  TestBuildStartedBy,
} from "@src/store/slices/testBuilds/types";

const initialState: TestBuildsState = { builds: {} };

const sameStarter = (a: TestBuildStartedBy, b: TestBuildStartedBy) =>
  a.type === b.type && a.key === b.key;

const testBuildsSlice = createSlice({
  name: "testBuilds",
  initialState,
  reducers: {
    /** A new build replaces the last one its starter ran. */
    buildStarted: (
      draft,
      action: PayloadAction<{
        id: string;
        startedBy: TestBuildStartedBy;
        name: string | null;
        typeLabel: string | null;
        steps: Pick<BuildStep, "id" | "label">[];
      }>
    ) => {
      const { id, startedBy, name, typeLabel, steps } = action.payload;
      for (const build of Object.values(draft.builds)) {
        if (sameStarter(build.startedBy, startedBy)) {
          delete draft.builds[build.id];
        }
      }
      draft.builds[id] = {
        id,
        startedBy,
        name,
        typeLabel,
        status: "running",
        steps: steps.map((step) => ({ ...step, status: "pending" })),
      };
    },
    /** Steps a running step found it needs, placed right after it. */
    stepsAdded: (
      draft,
      action: PayloadAction<{
        buildId: string;
        afterStepId: string;
        steps: Pick<BuildStep, "id" | "label">[];
      }>
    ) => {
      const build = draft.builds[action.payload.buildId];
      if (!build) {
        return;
      }
      const at = build.steps.findIndex(
        (step) => step.id === action.payload.afterStepId
      );
      build.steps.splice(
        at + 1,
        0,
        ...action.payload.steps.map((step) => ({
          ...step,
          status: "pending" as const,
        }))
      );
    },
    stepUpdated: (
      draft,
      action: PayloadAction<{
        buildId: string;
        stepId: string;
        status?: BuildStepStatus;
        detail?: string;
      }>
    ) => {
      const { buildId, stepId, status, detail } = action.payload;
      const step = draft.builds[buildId]?.steps.find((s) => s.id === stepId);
      if (!step) {
        return;
      }
      if (status) {
        step.status = status;
      }
      if (detail !== undefined) {
        step.detail = detail;
      }
    },
    buildFinished: (
      draft,
      action: PayloadAction<{
        buildId: string;
        status: "done" | "failed" | "cancelled";
      }>
    ) => {
      const build = draft.builds[action.payload.buildId];
      if (build) {
        build.status = action.payload.status;
      }
    },
  },
});

export const { buildStarted, stepsAdded, stepUpdated, buildFinished } =
  testBuildsSlice.actions;

export const testBuildsReducer = testBuildsSlice.reducer;

export const getTestBuild = (
  state: RootState,
  buildId: string
): TestBuild | null => state.testBuilds.builds[buildId] ?? null;

/** The last build this starter ran, or null. */
export const getTestBuildStartedBy = (
  state: RootState,
  startedBy: TestBuildStartedBy
): TestBuild | null =>
  Object.values(state.testBuilds.builds).find((build) =>
    sameStarter(build.startedBy, startedBy)
  ) ?? null;
